import {
  ArrowRight,
  CalendarClock,
  Check,
  ChevronRight,
  CirclePlay,
  FileCheck2,
  Fingerprint,
  LockKeyhole,
  MessagesSquare,
  PanelsTopLeft,
  Radio,
  ShieldCheck,
  Sparkles,
  Waypoints,
} from 'lucide-react';
import { Brand } from '../components/Brand';
import { PlatformBadge } from '../components/PlatformBadge';
import { StatusDot } from '../components/StatusDot';
import { drafts } from '../data';

interface LandingProps {
  onStart: () => void;
}

const plans = [
  {
    name: 'Starter',
    price: '$29',
    note: 'For one brand that wants a calm, consistent feed.',
    features: ['1 brand workspace', '4 connected channels', '60 scheduled posts / month', '500 assistant messages / month', 'Chat planning + approval flow'],
  },
  {
    name: 'Growth',
    price: '$79',
    note: 'For teams publishing every week across every channel.',
    features: ['3 brand workspaces', '12 connected channels', '300 scheduled posts / month', '2,500 assistant messages / month', 'Approval-first scheduling + delivery state'],
    featured: true,
  },
  {
    name: 'Agency',
    price: '$199',
    note: 'For multiple clients, leagues, and protected workspaces.',
    features: ['10 brand workspaces', '40 connected channels', '1,500 scheduled posts / month', '10,000 assistant messages / month', 'TKO league bundle eligibility'],
  },
];

export function Landing({ onStart }: LandingProps) {
  return (
    <div className="landing">
      <header className="site-header">
        <Brand />
        <nav className="site-nav" aria-label="Main navigation">
          <a href="#product">Product</a>
          <a href="#safety">Safety</a>
          <a href="#pricing">Pricing</a>
        </nav>
        <div className="site-actions">
          <button className="button button--ghost" onClick={onStart}>Log in</button>
          <a className="button button--dark" href="/app?demo=1">Try the app <ArrowRight size={15} /></a>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero__copy">
            <div className="eyebrow"><span /> Your social team, in one conversation</div>
            <h1>Say what you are doing.<br /><em>ThisBot handles the social plan.</em></h1>
            <p className="hero__lede">Connect the channels you already own. Give ThisBot the short version. It turns that into drafts, a schedule, and platform-ready recorded posts—with the exact account and settings in front of you before anything is approved.</p>
            <div className="hero__actions">
              <a className="button button--blue button--large" href="/app?demo=1">Start with one sentence <ArrowRight size={18} /></a>
              <a className="button button--outline button--large" href="#product"><CirclePlay size={18} /> See the workflow</a>
            </div>
            <div className="hero__proof">
              <span><ShieldCheck size={15} /> Tenant-isolated</span>
              <span><FileCheck2 size={15} /> Approval first</span>
              <span><LockKeyhole size={15} /> Your accounts</span>
            </div>
          </div>

          <div className="hero-product" aria-label="ThisBot application preview">
            <div className="hero-product__top">
              <div className="mini-brand"><img src="/brand/logo-primary.png" alt="" /><strong>ThisBot</strong></div>
              <StatusDot tone="green">Northstar · isolated</StatusDot>
              <span className="avatar">JG</span>
            </div>
            <div className="hero-product__body">
              <aside className="hero-product__rail">
                <span className="rail-mark is-active"><MessagesSquare size={16} /></span>
                <span className="rail-mark"><PanelsTopLeft size={16} /></span>
                <span className="rail-mark"><CalendarClock size={16} /></span>
                <span className="rail-mark"><Waypoints size={16} /></span>
              </aside>
              <div className="hero-chat">
                <p className="hero-chat__date">TODAY</p>
                <div className="preview-message preview-message--bot">
                  <img src="/brand/assistant-mark.png" alt="" />
                  <p>I found the strongest moment from Saturday. Want three versions for TikTok, Instagram, and YouTube?</p>
                </div>
                <div className="preview-message preview-message--user">Yes—make it feel like a comeback, not a recap.</div>
                <div className="preview-card">
                  <div>
                    <span className="preview-card__kicker">3 drafts ready</span>
                    <strong>The final round changed everything</strong>
                  </div>
                  <div className="preview-card__platforms">
                    <PlatformBadge provider="tiktok" />
                    <PlatformBadge provider="instagram" />
                    <PlatformBadge provider="youtube" />
                  </div>
                  <button>Review exact posts <ChevronRight size={14} /></button>
                </div>
                <div className="preview-composer"><span>Tell ThisBot what happens next…</span><button><ArrowRight size={15} /></button></div>
              </div>
              <aside className="hero-plan">
                <span className="hero-plan__label">THIS WEEK</span>
                <strong>7 approved</strong>
                <div className="hero-plan__bar"><span style={{ width: '68%' }} /></div>
                <p>Two open moments are worth filling.</p>
                <div className="hero-plan__draft">
                  <span>Tonight · 6:30</span>
                  <strong>{drafts[0]?.title}</strong>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="channel-strip" aria-label="Supported channels">
          <p>One plan, adapted for each connected destination</p>
          <div>
            <PlatformBadge provider="tiktok" label />
            <PlatformBadge provider="facebook" label />
            <PlatformBadge provider="instagram" label />
            <PlatformBadge provider="youtube" label />
          </div>
        </section>

        <section className="section" id="product">
          <div className="section-heading">
            <span className="section-index">01 / HOW IT WORKS</span>
            <h2>Less dashboard.<br />More conversation.</h2>
            <p>You do not have to learn a marketing system. ThisBot asks for the missing pieces, builds the work, and puts every consequential action in a clear approval card.</p>
          </div>
          <div className="steps-grid">
            <article>
              <span className="step-number">01</span>
              <MessagesSquare size={24} />
              <h3>Tell it the short version</h3>
              <p>“We have semifinals Saturday and want more players next season” is enough to begin.</p>
            </article>
            <article>
              <span className="step-number">02</span>
              <Sparkles size={24} />
              <h3>Review what it makes</h3>
              <p>Platform-specific captions, titles, hashtags, disclosures, and a suggested cadence for recorded posts.</p>
            </article>
            <article>
              <span className="step-number">03</span>
              <CalendarClock size={24} />
              <h3>Approve the exact schedule</h3>
              <p>See the account, preview, privacy, time, and platform controls. Then approve or edit.</p>
            </article>
          </div>
        </section>

        <section className="section section--split">
          <div className="feature-visual">
            <div className="feature-window">
              <div className="feature-window__head"><span /><span /><span /><small>Approval snapshot</small></div>
              <div className="approval-preview">
                <div className="approval-preview__media"><CirclePlay size={38} /><span>0:34 vertical preview</span></div>
                <div className="approval-preview__copy">
                  <span className="blue-label">READY FOR YOUR OK</span>
                  <h4>The final round changed everything.</h4>
                  <p>Three angles. One impossible comeback. Watch the moment the bracket flipped.</p>
                  <div className="approval-row"><PlatformBadge provider="tiktok" label /><strong>@northstarleague</strong></div>
                  <div className="approval-row"><span>Privacy</span><strong>Choose before approval</strong></div>
                  <button className="button button--blue">Review & schedule</button>
                </div>
              </div>
            </div>
          </div>
          <div className="feature-copy">
            <span className="section-index">02 / CONTROL</span>
            <h2>Automation that shows its work.</h2>
            <p>ThisBot can prepare nearly everything. Publishing is intentionally explicit: no mystery account, surprise privacy setting, or silent cross-post.</p>
            <ul className="check-list">
              <li><Check size={16} /> Every post is bound to one tenant and destination account</li>
              <li><Check size={16} /> TikTok privacy and interaction settings are never preselected</li>
              <li><Check size={16} /> Scheduled versions are immutable after approval</li>
              <li><Check size={16} /> A complete audit trail follows each delivery</li>
            </ul>
          </div>
        </section>

        <section className="security-section" id="safety">
          <div>
            <span className="section-index section-index--light">03 / BUILT FOR MULTIPLE CLIENTS</span>
            <h2>Your clients share a product.<br />They never share data.</h2>
            <p>Every customer, account token, asset, chat, schedule, queue message, and provider result is bound to an immutable tenant boundary. Missing credentials stop the job; the system never falls back to another customer.</p>
          </div>
          <div className="security-grid">
            <article><Fingerprint size={22} /><strong>Server-resolved tenancy</strong><p>The browser and AI cannot choose or override the security context.</p></article>
            <article><LockKeyhole size={22} /><strong>KMS-encrypted tokens</strong><p>Provider credentials are authenticated to one tenant and one connection.</p></article>
            <article><Waypoints size={22} /><strong>Provider-separated queues</strong><p>One account or platform outage cannot leak into another delivery path.</p></article>
            <article><ShieldCheck size={22} /><strong>Negative isolation tests</strong><p>Guessed IDs, callback swapping, webhook replay, and redelivery are tested to fail closed.</p></article>
          </div>
        </section>

        <section className="section live-section">
          <div className="section-heading section-heading--wide">
            <span className="section-index">04 / LAUNCH SCOPE</span>
            <h2>Recorded posts first. Live control later.</h2>
            <p>ThisBot’s launch scope is recorded-media planning, review, scheduling, and delivery state. Live-broadcast creation, encoder ingest details, stream monitoring, and automated live start are not enabled.</p>
          </div>
          <div className="live-cards">
            <article><CalendarClock size={21} /><strong>Recorded scheduled publishing</strong><span>Review exact media, destination, copy, disclosures, privacy, and time.</span><StatusDot tone="blue">Launch scope</StatusDot></article>
            <article><Radio size={21} /><strong>Live broadcasts</strong><span>Create and manage broadcasts in the provider’s own tools.</span><StatusDot tone="gray">Not enabled</StatusDot></article>
          </div>
        </section>

        <section className="pricing-section" id="pricing">
          <div className="section-heading section-heading--center">
            <span className="section-index">05 / PRICING</span>
            <h2>Start small. Keep the boundary.</h2>
            <p>Launch pricing is wired through server-side Stripe products, so plans can change without rebuilding the app.</p>
          </div>
          <div className="pricing-grid">
            {plans.map((plan) => (
              <article key={plan.name} className={plan.featured ? 'is-featured' : ''}>
                {plan.featured && <span className="popular">MOST USEFUL</span>}
                <h3>{plan.name}</h3>
                <p className="price"><strong>{plan.price}</strong><span>/ month</span></p>
                <p className="plan-note">{plan.note}</p>
                <ul>{plan.features.map((feature) => <li key={feature}><Check size={15} />{feature}</li>)}</ul>
                <a className={`button ${plan.featured ? 'button--blue' : 'button--outline'}`} href="/app?demo=1">Start with {plan.name}</a>
              </article>
            ))}
          </div>
          <p className="pricing-fineprint">Prices are launch defaults. Provider approval, account eligibility, API usage, and storage limits still apply.</p>
        </section>

        <section className="final-cta">
          <img src="/brand/logo-primary.png" alt="" />
          <div><span>YOU ALREADY KNOW ENOUGH TO START</span><h2>Tell ThisBot what is happening next.</h2></div>
          <a className="button button--blue button--large" href="/app?demo=1">Open the conversation <ArrowRight size={18} /></a>
        </section>
      </main>

      <footer className="site-footer">
        <Brand />
        <p>Chat-first social planning and publishing for teams that want control without the clutter.</p>
        <nav aria-label="Legal">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/data-deletion">Data deletion</a>
          <a href="mailto:support@thisbotdoesmysocialmedia.com">Support</a>
        </nav>
        <small>© {new Date().getFullYear()} ThisBotDoesMySocialMedia.com</small>
      </footer>
    </div>
  );
}
